import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { IGrant } from '~/types/campaigns';
import {
  MdNavigateBefore,
  MdOutlineEdit,
  MdNavigateNext,
  MdDragHandle,
} from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import AddGrantDrawer from '~/components/Grants/AddGrantDrawer';

const columnHelper = createColumnHelper<IGrant & { action?: string }>();

const columns = [
  columnHelper.accessor('name', {
    header: () => 'Grant Name',
    cell: (props) => (
      <div>
        <input type='checkbox' className='mr-4' />
        <Link className='font-bold text-primary' to={`/`}>
          {props.renderValue()}
        </Link>
      </div>
    ),
  }),
  columnHelper.accessor('action', {
    header: () => '',
    cell: () => (
      <div className='flex justify-end gap-3'>
        <MdDragHandle size={20} />
        <MdOutlineEdit className='text-primary' size={20} />
        <RiDeleteBinLine className='text-red' size={20} />
      </div>
    ),
  }),
];

enum Sort {
  ASC = 'asc',
  DESC = 'desc',
}

const obj = {
  name: 'Annie McMahon Memorial Fund',
};

const GrantSettings: React.FC = () => {
  const [sort, setSort] = useState<Sort | null>(null);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [isAddGrantShowing, setIsAddGrantShowing] = useState(false);

  const data = useMemo(
    () =>
      Array.from({ length: 10 }, (_, index) => ({
        id: index + 1,
        ...obj,
        isPinned: index % 2 === 0,
      })),
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSort(sort === Sort.ASC ? Sort.DESC : Sort.ASC);
    } else {
      setSortBy(columnId);
      setSort(Sort.ASC);
    }
  };

  return (
    <>
      <Breadcrumb pageName='Grant Settings' />

      <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
        <div className='flex items-center justify-end gap-4 px-8 pb-4'>
          <button
            type='button'
            className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-[#F4F4F5] bg-[#F4F4F5] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#949599] transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
          >
            Delete (0)
          </button>
          <button
            type='button'
            className='inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
            onClick={() => setIsAddGrantShowing(true)}
          >
            Add
          </button>
        </div>

        <div
          className='flex items-center justify-between border-t border-stroke px-8 py-1'
          style={{ fontSize: '12px' }}
        >
          <p>Showing {data.length} results</p>

          <div className='flex items-center gap-5'>
            <div className='inline-flex items-center justify-center gap-1.5'>
              <MdOutlineEdit className='text-primary' size={18} />
              Edit
            </div>
            <div className='inline-flex items-center justify-center gap-1.5'>
              <RiDeleteBinLine className='text-red' size={18} />
              Delete
            </div>
          </div>
        </div>

        <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:table-fixed md:overflow-auto md:px-8'>
          <thead className='border-separate px-4'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                className='border-t border-stroke bg-whiten'
                style={{ fontSize: '14px' }}
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => (
                  <th
                    style={{
                      paddingTop: '16px',
                      paddingBottom: '16px',
                    }}
                    key={header.id}
                  >
                    <div className='flex items-center'>
                      <span
                        onClick={() => handleSort(header.column.id)}
                        className='cursor-pointer'
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr className='border-t border-stroke' key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{
                        paddingTop: '16px',
                        paddingBottom: '16px',
                      }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className='flex justify-between border-t border-stroke px-8 pt-5'>
          <p className='font-light'>Showing {data.length} results</p>
          <div className='flex'>
            <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
              <MdNavigateBefore size={20} />
            </button>

            <button className='mx-1 flex cursor-pointer items-center justify-center rounded-md bg-primary p-1 px-3 hover:bg-primary'>
              1
            </button>
            <button className='flex cursor-pointer items-center justify-center rounded-md p-1 px-2 hover:bg-primary'>
              <MdNavigateNext size={20} />
            </button>
          </div>
        </div>
      </section>
      <AddGrantDrawer
        isOpen={isAddGrantShowing}
        setIsOpen={setIsAddGrantShowing}
      />
    </>
  );
};

export default GrantSettings;
